import React from 'react';
import { Link } from 'react-router-dom';
import useSeo from '../hooks/useSeo';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';
import {
  BookOpen, Brain, GraduationCap, Lightbulb, Layers, Scale,
  Users, Target, ArrowLeft, ChevronRight, CheckCircle2, XCircle,
  AlertTriangle, Sparkles, Heart, Trophy,
} from 'lucide-react';
import {
  META_FONDAMENTS, CADRE_OFFICIEL, MATRICE_GDE, REMC,
  PEDAGOGIE_PAR_OBJECTIFS, METHODES_PEDAGOGIQUES, EVALUATIONS,
  RELATION_PEDAGOGIQUE, PYRAMIDE_MASLOW, DEFINITIONS_COMPLEMENTAIRES,
} from '../data/fondamentsPedagogie';

export default function FondamentsPedagogie() {
  useSeo({
    title: 'Fondements pédagogiques — Cadre français de la formation conducteur',
    description: 'Cadre officiel de la pédagogie en formation du conducteur (Benninger 2022) : REMC, matrice GDE, PPO, méthodes pédagogiques, évaluations, relation pédagogique, Maslow. Conforme au cours TP-ECSR CCR1.',
    path: '/enseignant/fondements-pedagogie',
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="fondements-pedagogie-page">
      {/* En-tête */}
      <header className="border-b border-border bg-gradient-to-br from-primary/5 via-background to-background py-8 px-4 md:px-6">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link to="/enseignant/dashboard" className="hover:text-primary inline-flex items-center gap-1" data-testid="back-to-dashboard">
              <ArrowLeft className="w-3 h-3" />
              Espace enseignant
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span>Fondements pédagogiques</span>
          </div>

          <div className="space-y-2">
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider" data-testid="badge-source">
              {META_FONDAMENTS.source}
            </Badge>
            <h1 className="text-2xl md:text-4xl font-bold leading-tight flex items-center gap-3">
              <Brain className="w-8 h-8 text-primary flex-shrink-0" />
              {META_FONDAMENTS.titre}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground max-w-3xl">
              {META_FONDAMENTS.contexte}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 mt-6">
        <Tabs defaultValue="cadre" className="space-y-6">
          <TabsList className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 w-full h-auto" data-testid="tabs-list">
            <TabsTrigger value="cadre" className="text-[11px]" data-testid="tab-cadre">📋 Cadre</TabsTrigger>
            <TabsTrigger value="gde" className="text-[11px]" data-testid="tab-gde">🎯 Matrice GDE</TabsTrigger>
            <TabsTrigger value="remc" className="text-[11px]" data-testid="tab-remc">📘 REMC</TabsTrigger>
            <TabsTrigger value="ppo" className="text-[11px]" data-testid="tab-ppo">🧭 PPO + Objectifs</TabsTrigger>
            <TabsTrigger value="methodes" className="text-[11px]" data-testid="tab-methodes">🛠️ Méthodes</TabsTrigger>
            <TabsTrigger value="evaluation" className="text-[11px]" data-testid="tab-evaluation">📊 Évaluation</TabsTrigger>
            <TabsTrigger value="relation" className="text-[11px]" data-testid="tab-relation">🤝 Relation</TabsTrigger>
          </TabsList>

          {/* === TAB CADRE OFFICIEL === */}
          <TabsContent value="cadre" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Layers className="w-4 h-4 text-primary" />
                  {CADRE_OFFICIEL.intitule}
                </CardTitle>
                <CardDescription>{CADRE_OFFICIEL.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Évolution chronologique</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {CADRE_OFFICIEL.evolution.map((e, i) => (
                      <div key={i} className="rounded-md border border-border bg-muted/30 p-3">
                        <div className="text-[10px] uppercase font-bold text-primary">{e.date}</div>
                        <div className="text-xs mt-1">{e.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <Section icon={BookOpen} title={CADRE_OFFICIEL.livretApprentissage.intitule}>
                  <ul className="space-y-1 text-sm">
                    {CADRE_OFFICIEL.livretApprentissage.competences.map((c, i) => (
                      <li key={i} className="flex gap-2"><ChevronRight className="w-3 h-3 mt-1 text-primary flex-shrink-0" />{c}</li>
                    ))}
                  </ul>
                  <p className="text-[11px] italic text-amber-700 dark:text-amber-400 mt-2 flex gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5" />
                    {CADRE_OFFICIEL.livretApprentissage.note}
                  </p>
                </Section>
              </CardContent>
            </Card>
          </TabsContent>

          {/* === TAB MATRICE GDE === */}
          <TabsContent value="gde" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target className="w-4 h-4 text-primary" />
                  {MATRICE_GDE.intitule}
                </CardTitle>
                <CardDescription>{MATRICE_GDE.origine}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {MATRICE_GDE.niveaux.slice().reverse().map((n) => (
                    <div key={n.numero} className="rounded-md border border-border bg-muted/20 p-3" data-testid={`gde-niveau-${n.numero}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-md bg-primary/10 text-primary font-bold text-sm flex items-center justify-center flex-shrink-0">
                          N{n.numero}
                        </div>
                        <div>
                          <div className="font-semibold text-sm">{n.intitule}</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {n.enjeux.map((e, i) => <Badge key={i} variant="outline" className="text-[10px]">{e}</Badge>)}
                          </div>
                        </div>
                      </div>
                      <p className="text-xs leading-relaxed mb-2">{n.description}</p>
                      <div className="text-[11px] text-muted-foreground">
                        <span className="font-semibold uppercase">Méthodes adaptées : </span>
                        {n.methodes.join(' • ')}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs italic text-muted-foreground mt-4 border-t pt-3">{MATRICE_GDE.note}</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* === TAB REMC === */}
          <TabsContent value="remc" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <BookOpen className="w-4 h-4 text-primary" />
                  {REMC.intitule}
                </CardTitle>
                <CardDescription>Entrée en vigueur : {REMC.dateEntreeVigueur}. {REMC.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Section icon={AlertTriangle} title="8 enjeux éducatifs prioritaires">
                  <ul className="grid sm:grid-cols-2 gap-1.5 text-xs">
                    {REMC.enjeuxEducatifs.map((e, i) => (
                      <li key={i} className="flex gap-2"><ChevronRight className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />{e}</li>
                    ))}
                  </ul>
                </Section>

                <Section icon={Sparkles} title="4 grandes notions du REMC">
                  <div className="grid sm:grid-cols-2 gap-2">
                    {REMC.quatreNotions.map((n, i) => (
                      <div key={i} className="rounded-md bg-primary/5 border border-primary/20 px-3 py-2 text-xs font-medium">{n}</div>
                    ))}
                  </div>
                </Section>

                <Section icon={Target} title="4 compétences globales du REMC" highlight>
                  <div className="space-y-2">
                    {REMC.competencesGlobales.map((c) => (
                      <div key={c.numero} className="border-l-2 border-primary pl-3 text-sm">
                        <div className="font-semibold flex items-center gap-2">
                          <Badge variant="default" className="text-[10px]">C{c.numero}</Badge>
                          <Badge variant="outline" className="text-[10px]">Niveau GDE {c.lienNiveauGDE}</Badge>
                        </div>
                        <p className="mt-1 text-foreground/90">{c.intitule}</p>
                      </div>
                    ))}
                  </div>
                </Section>

                <p className="text-xs italic text-amber-700 dark:text-amber-400 border-t pt-3">{REMC.note}</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* === TAB PPO === */}
          <TabsContent value="ppo" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target className="w-4 h-4 text-primary" />
                  {PEDAGOGIE_PAR_OBJECTIFS.intitule}
                </CardTitle>
                <CardDescription>{PEDAGOGIE_PAR_OBJECTIFS.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Section icon={Lightbulb} title="Principes fondateurs">
                  <ul className="space-y-1 text-sm">
                    {PEDAGOGIE_PAR_OBJECTIFS.principes.map((p, i) => (
                      <li key={i} className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-primary flex-shrink-0" />{p}</li>
                    ))}
                  </ul>
                </Section>

                <div className="space-y-3">
                  {PEDAGOGIE_PAR_OBJECTIFS.typesObjectifs.map((o) => (
                    <div key={o.type} className="rounded-md border border-border bg-muted/20 p-3 space-y-2" data-testid={`objectif-${o.type}`}>
                      <div className="font-semibold text-sm flex items-center gap-2">
                        <Badge variant="default" className="text-[10px] uppercase">{o.type}</Badge>
                        {o.intitule}
                      </div>
                      <p className="text-xs leading-relaxed">{o.description}</p>
                      {o.qualites && (
                        <div className="flex flex-wrap gap-1">
                          {o.qualites.map((q, i) => <Badge key={i} variant="outline" className="text-[10px]">{q}</Badge>)}
                        </div>
                      )}
                      {o.verbesAUtiliser && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                          <div className="rounded bg-emerald-500/10 border border-emerald-500/20 p-2">
                            <div className="font-bold text-emerald-700 dark:text-emerald-300 mb-1">✓ À utiliser</div>
                            <div>{o.verbesAUtiliser.join(' · ')}</div>
                          </div>
                          <div className="rounded bg-destructive/10 border border-destructive/20 p-2">
                            <div className="font-bold text-destructive mb-1">✗ À éviter</div>
                            <div>{o.verbesAEviter.join(' · ')}</div>
                          </div>
                        </div>
                      )}
                      {o.structure && (
                        <div className="text-[11px] italic font-mono bg-muted px-2 py-1 rounded">{o.structure}</div>
                      )}
                      <div className="text-[11px] bg-primary/5 border border-primary/20 rounded p-2">
                        <span className="font-bold">Exemple : </span>{o.exemple}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* === TAB MÉTHODES === */}
          <TabsContent value="methodes" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <BookOpen className="w-4 h-4 text-primary" />
                  {METHODES_PEDAGOGIQUES.intitule}
                </CardTitle>
                <CardDescription>{METHODES_PEDAGOGIQUES.definition}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {METHODES_PEDAGOGIQUES.familles.map((f) => (
                  <div key={f.code} className="space-y-3" data-testid={`famille-${f.code}`}>
                    <div className="rounded-md bg-primary/5 border border-primary/20 p-3">
                      <div className="font-semibold text-sm flex items-center gap-2">
                        <Layers className="w-4 h-4 text-primary" />
                        {f.intitule}
                        <Badge variant="outline" className="text-[10px] ml-auto">Rétention {f.pourcentageRetention}</Badge>
                      </div>
                      <p className="text-xs mt-1 text-muted-foreground">{f.description}</p>
                    </div>

                    <Accordion type="multiple" className="space-y-2">
                      {f.methodes.map((m) => (
                        <AccordionItem key={m.code} value={m.code} className="border border-border rounded-md overflow-hidden" data-testid={`methode-${m.code}`}>
                          <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-muted/30">
                            <div className="text-left text-sm font-semibold">{m.intitule}</div>
                          </AccordionTrigger>
                          <AccordionContent className="px-3 pt-2 pb-3 space-y-2">
                            <p className="text-xs leading-relaxed">{m.description}</p>
                            <div className="grid md:grid-cols-2 gap-2">
                              <SubList icon={CheckCircle2} title="Avantages" items={m.avantages} color="emerald" />
                              <SubList icon={XCircle} title="Inconvénients" items={m.inconvenients} color="rose" />
                            </div>
                            {m.solutions && (
                              <SubList icon={Lightbulb} title="Solutions / remédiations" items={m.solutions} color="amber" />
                            )}
                            {m.structure && (
                              <div className="rounded bg-muted px-2 py-1.5 text-[11px] italic">
                                <span className="font-bold">Structure : </span>{m.structure}
                              </div>
                            )}
                            <div className="text-[11px] bg-primary/5 border border-primary/20 rounded p-2">
                              <span className="font-bold">👉 Quand l'utiliser : </span>{m.quandUtiliser}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* === TAB ÉVALUATION === */}
          <TabsContent value="evaluation" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Scale className="w-4 h-4 text-primary" />
                  {EVALUATIONS.intitule}
                </CardTitle>
                <CardDescription>{EVALUATIONS.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {EVALUATIONS.typesParMoment.map((m) => (
                  <div key={m.moment} className="space-y-2">
                    <div className="text-xs font-bold uppercase text-primary">{m.moment}</div>
                    {m.types.map((t) => (
                      <div key={t.code} className="rounded-md border border-border bg-muted/20 p-3 space-y-2" data-testid={`eval-${t.code}`}>
                        <div className="font-semibold text-sm">{t.intitule}</div>
                        <div className="grid sm:grid-cols-3 gap-2 text-[11px]">
                          <Info label="Quand" value={t.quand} />
                          <Info label="But" value={t.but} />
                          <Info label="Conséquence" value={t.consequence} />
                        </div>
                        <div className="text-[11px]">
                          <span className="font-bold uppercase text-muted-foreground">Outils : </span>
                          <span>{t.outils.join(' · ')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

                <Section icon={Lightbulb} title={EVALUATIONS.questionnement.intitule} highlight>
                  <p className="text-xs mb-2">{EVALUATIONS.questionnement.description}</p>
                  <div className="grid gap-2">
                    {EVALUATIONS.questionnement.types.map((q, i) => (
                      <div key={i} className="rounded border border-border bg-background p-2 text-xs">
                        <div className="font-semibold">Question {q.type}</div>
                        <div className="text-muted-foreground mt-0.5">{q.description}</div>
                        <div className="italic mt-1">« {q.exemple} »</div>
                        <div className="text-[10px] text-primary mt-1 font-semibold uppercase">Usage : {q.usage}</div>
                      </div>
                    ))}
                  </div>
                </Section>
              </CardContent>
            </Card>
          </TabsContent>

          {/* === TAB RELATION === */}
          <TabsContent value="relation" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="w-4 h-4 text-primary" />
                  {RELATION_PEDAGOGIQUE.intitule}
                </CardTitle>
                <CardDescription>{RELATION_PEDAGOGIQUE.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Section icon={Layers} title={RELATION_PEDAGOGIQUE.triangle.intitule}>
                  <p className="text-xs mb-2">{RELATION_PEDAGOGIQUE.triangle.description}</p>
                  <div className="grid sm:grid-cols-3 gap-2">
                    {RELATION_PEDAGOGIQUE.triangle.faces.map((f, i) => (
                      <div key={i} className="rounded-md border border-border bg-muted/20 p-2.5 text-xs">
                        <div className="font-bold text-primary">{f.axe}</div>
                        <div className="text-muted-foreground text-[10px] mt-0.5">{f.poles}</div>
                        <div className="text-amber-700 dark:text-amber-400 text-[10px] mt-1 italic">⚠ {f.risque}</div>
                      </div>
                    ))}
                  </div>
                </Section>

                <div className="grid md:grid-cols-2 gap-3">
                  <Section icon={GraduationCap} title={RELATION_PEDAGOGIQUE.roles.formateur.intitule}>
                    <div className="text-[10px] uppercase font-semibold text-muted-foreground mt-1">Missions</div>
                    <ul className="space-y-0.5 text-xs">
                      {RELATION_PEDAGOGIQUE.roles.formateur.missions.map((m, i) => (
                        <li key={i} className="flex gap-2"><ChevronRight className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />{m}</li>
                      ))}
                    </ul>
                    <div className="text-[10px] uppercase font-semibold text-muted-foreground mt-3">Postures</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {RELATION_PEDAGOGIQUE.roles.formateur.postures.map((p, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]">{p}</Badge>
                      ))}
                    </div>
                  </Section>
                  <Section icon={Heart} title={RELATION_PEDAGOGIQUE.roles.eleve.intitule}>
                    <ul className="space-y-0.5 text-xs">
                      {RELATION_PEDAGOGIQUE.roles.eleve.attitudes.map((a, i) => (
                        <li key={i} className="flex gap-2"><ChevronRight className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />{a}</li>
                      ))}
                    </ul>
                  </Section>
                </div>

                <Section icon={Sparkles} title={RELATION_PEDAGOGIQUE.roles.lien.intitule} highlight>
                  <p className="text-sm italic">{RELATION_PEDAGOGIQUE.roles.lien.element}</p>
                </Section>

                {/* MASLOW */}
                <Section icon={Trophy} title={PYRAMIDE_MASLOW.intitule}>
                  <p className="text-xs mb-3">{PYRAMIDE_MASLOW.description}</p>
                  <div className="space-y-1.5">
                    {PYRAMIDE_MASLOW.niveaux.map((n) => (
                      <div key={n.ordre} className="rounded-md border border-border bg-muted/20 px-3 py-2 text-xs" style={{ marginLeft: `${(5 - n.ordre) * 8}px`, marginRight: `${(5 - n.ordre) * 8}px` }}>
                        <div className="flex items-center gap-2">
                          <Badge variant="default" className="text-[10px]">{n.ordre}</Badge>
                          <span className="font-semibold">{n.intitule}</span>
                        </div>
                        <div className="mt-1 text-muted-foreground">{n.description}</div>
                        <div className="mt-1 text-[10px] italic text-primary">{n.exempleConduite}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-[10px] uppercase font-semibold text-muted-foreground">Applications pédagogiques</div>
                  <ul className="space-y-0.5 text-xs mt-1">
                    {PYRAMIDE_MASLOW.applicationsPedagogiques.map((a, i) => (
                      <li key={i} className="flex gap-2"><ChevronRight className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />{a}</li>
                    ))}
                  </ul>
                </Section>

                {/* Définitions complémentaires */}
                <Section icon={BookOpen} title="Définitions complémentaires">
                  <Accordion type="multiple" className="space-y-2">
                    {DEFINITIONS_COMPLEMENTAIRES.map((d) => (
                      <AccordionItem key={d.terme} value={d.terme} className="border border-border rounded-md overflow-hidden">
                        <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-muted/30 text-sm font-semibold">
                          {d.terme}
                        </AccordionTrigger>
                        <AccordionContent className="px-3 pt-1 pb-3 space-y-2 text-xs">
                          <p>{d.definition}</p>
                          <div className="bg-muted/40 rounded p-2 italic">
                            <span className="font-bold">Exemple : </span>{d.exemple}
                          </div>
                          <div className="text-[11px] text-primary font-semibold uppercase">💡 Usage pédagogique :</div>
                          <p>{d.usage}</p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Section>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* CTA */}
        <Card className="bg-gradient-to-br from-primary/5 to-background border-primary/20 mt-6">
          <CardContent className="py-6 text-center space-y-3">
            <h2 className="text-lg font-semibold">Mobiliser ces fondements devant le jury CCS</h2>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              Citez REMC, GDE, PPO et les méthodes pédagogiques pour structurer vos réponses lors de l'entretien technique. Liens utiles :
            </p>
            <div className="flex flex-wrap justify-center gap-2 pt-2">
              <Button asChild size="sm" variant="default" data-testid="cta-referentiel">
                <Link to="/enseignant/referentiel">📖 Référentiel CCS</Link>
              </Button>
              <Button asChild size="sm" variant="outline" data-testid="cta-etudes">
                <Link to="/enseignant/etudes-de-cas">📚 27 études de cas</Link>
              </Button>
              <Button asChild size="sm" variant="outline" data-testid="cta-guide">
                <Link to="/enseignant/guide">🎓 Guide ECSR</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// ---- Sous-composants ----
function Section({ icon: Icon, title, children, highlight = false }) {
  return (
    <div className={`rounded-md ${highlight ? 'bg-primary/5 border border-primary/20' : 'bg-muted/30 border border-border/50'} p-3 space-y-2`}>
      <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-foreground/80">
        <Icon className="w-4 h-4 text-primary" />
        {title}
      </h3>
      <div>{children}</div>
    </div>
  );
}

function SubList({ icon: Icon, title, items, color = 'primary' }) {
  const colorMap = {
    emerald: 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/5 border-emerald-500/20',
    rose: 'text-rose-700 dark:text-rose-400 bg-rose-500/5 border-rose-500/20',
    amber: 'text-amber-700 dark:text-amber-400 bg-amber-500/5 border-amber-500/20',
    primary: 'text-primary bg-primary/5 border-primary/20',
  };
  return (
    <div className={`rounded border p-2 ${colorMap[color] || colorMap.primary}`}>
      <div className="text-[10px] uppercase font-bold flex items-center gap-1 mb-1">
        <Icon className="w-3 h-3" />
        {title}
      </div>
      <ul className="space-y-0.5 text-[11px]">
        {items.map((it, i) => (
          <li key={i} className="flex gap-1.5"><span>•</span><span>{it}</span></li>
        ))}
      </ul>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded border border-border/50 bg-background px-2 py-1.5">
      <div className="text-[10px] text-muted-foreground uppercase font-semibold">{label}</div>
      <div className="text-[11px] font-medium leading-snug">{value}</div>
    </div>
  );
}
